import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { encrypt, YYMMDD, timeView } from "../../helpers/utilities";
import { downloadLink } from '../../helpers/constFIles';
import { URLS } from "../../helpers/URLS";
import useAxios from "../../helpers/Hooks/useAxios";
import SkeletonLoader from "../../loaders/Skeleton";
import NoData from "../../loaders/NoData";
import CancelIcon from "@mui/icons-material/Cancel"
import ErrorIcon from "@mui/icons-material/Error"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import ResumeViewModal from "../Recruiter/ResumeViewModal";
import Button from '@mui/material/Button';
import PaginationGlobal from "../TablePagination.js/PaginationGlobal";
import AssessmentIcon from '@mui/icons-material/Assessment';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import SearchIcon from '@mui/icons-material/Search';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { TextField, InputAdornment, IconButton, Tooltip } from '@mui/material';
import { matchesSearch } from '../../helpers/search';
import '../../assets/scss/tables.scss';

/* what each provider is called, where a link is shown */
const PROVIDER_NAMES = {
    zoom: 'Zoom',
    teams: 'Teams',
    google: 'Google Meet',
    jitsi: 'Jitsi Meet',
};

/* everything visible on a row is searchable */
const SEARCH_FIELDS = [
    'candidateID', 'name', 'email', 'phone',
    'skills', 'roundType', 'status',
];

const InterViewAsssignList = () => {

    const { fetchData } = useAxios();

    const [resumeView, setResumeView] = useState({
        filePath: "",
        filetype: "",
        resumeName: "",
    });

    const [pageNumber, setPageNumber] = useState(1);

    const [interViewList, setInterViewList] = useState([]);

    const [resumeShow, setResumeShow] = useState(false);

    const [loadSkeleton, setLoadSkeleton] = useState(true);

    useEffect(() => {
        getList()
    }, [])

    const getList = async () => {
        setLoadSkeleton(true);
        const resp = await fetchData({ url: `${URLS.assignInterViewList}`, method: 'get' });
        if (resp.status === 200) {
            setInterViewList(resp.data);
            setLoadSkeleton(false);
        }
    }

    const candidateResume = (data) => {
        setResumeView({
            ...resumeView,
            filePath: downloadLink + data.resumeUrl,
            filetype: data.resumeUrl.split(".").at(-1),
            resumeName: data.name,
        });
        setResumeShow(true);
    };
   
    const resumeHide = () => {
        setResumeShow(false);
    };

    const pull_data = (data) => {
        setPageNumber(data)
    }

    const [rowsPerPage, setRowsPerPage] = useState(15);

    const [search, setSearch] = useState('');

    /* the candidate sits one level down, so the searchable values are lifted
       onto each row before matching */
    const rows = interViewList.map((cand) => ({
        ...cand,
        candidateID: cand?.candidate?.candidateID,
        name: cand?.candidate?.name,
        email: cand?.candidate?.email,
        phone: cand?.candidate?.phone,
        skills: cand?.candidate?.skills,
    }));

    const searchResult = rows.filter((row) => matchesSearch(row, search, SEARCH_FIELDS));

    useEffect(() => { setPageNumber(1); }, [search]);

    const statusTone = (status) =>
        status === 'Selected' ? 'wh-pill-good'
            : status === 'Rejected' ? 'wh-pill-danger'
                : status === 'Hold' ? 'wh-pill-warn' : '';

    const statusIcon = (status) =>
        status === 'Selected' ? <CheckCircleIcon />
            : status === 'Rejected' ? <CancelIcon />
                : status === 'Hold' ? <ErrorIcon /> : <AssessmentIcon />;

    return (
        <div className="wh-table iv-list">
            <div className="wh-toolbar">
                <h4 className="title">My Interviews</h4>

                <div className="wh-toolbar-controls">
                    <TextField
                        placeholder="Search name, email, skill, round…"
                        size="small"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="wh-search-field"
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
                            endAdornment: search
                                ? <InputAdornment position="end">
                                    <IconButton size="small" className="wh-search-clear"
                                        aria-label="Clear search" onClick={() => setSearch('')}>
                                        <CloseRoundedIcon fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                                : null,
                        }}
                    />
                </div>
            </div>

            {loadSkeleton ? <SkeletonLoader /> :
                (searchResult.length > 0 ?
                    <>
                        <div className="table-responsive wh-table-shell">
                            <table className="table">
                                <thead>
                                    <tr style={{ whiteSpace: 'nowrap' }}>
                                        <th scope="col" className="wh-col-shrink">ID</th>
                                        <th scope="col">Name</th>
                                        <th scope="col">Email</th>
                                        <th scope="col" className="wh-col-shrink">Phone</th>
                                        <th scope="col" className="wh-col-shrink">Experience</th>
                                        <th scope="col">Skills</th>
                                        <th scope="col" className="wh-col-shrink">Date &amp; Time</th>
                                        <th scope="col" className="wh-col-shrink">Round</th>
                                        <th scope="col" className="wh-pin-3 wh-pin-edge">Meeting</th>
                                        <th scope="col" className="wh-pin-2">Status</th>
                                        <th scope="col" className="wh-pin-1 text-center">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {searchResult.slice((pageNumber - 1) * rowsPerPage, pageNumber * rowsPerPage).map((cand, index) => {
                                        return (
                                            <tr key={index}>
                                                <td className="wh-col-shrink">{cand.candidateID}</td>
                                                <td>{cand.name}</td>
                                                <td>{cand.email}</td>
                                                <td className="wh-col-shrink">{cand.phone}</td>
                                                <td className="wh-col-shrink">{cand.candidate?.experience} Years</td>
                                                <td>
                                                    <span className="wh-cell-clamp" title={cand.skills}>{cand.skills}</span>
                                                </td>
                                                <td className="wh-col-shrink">
                                                    <span className="iv-when">
                                                        <b>{YYMMDD(cand.startTime)}</b>
                                                        <em>{timeView(cand.startTime)} &ndash; {timeView(cand.endTime)}</em>
                                                    </span>
                                                </td>
                                                <td className="wh-col-shrink">
                                                    <span className="iv-round">
                                                        <b>Round {cand.round}</b>
                                                        <em>{cand.roundType}</em>
                                                    </span>
                                                </td>
                                                <td className="wh-pin-3 wh-pin-edge">
                                                    {cand.meetingUrl
                                                        ? <a className="iv-join" href={cand.meetingUrl}
                                                            target="_blank" rel="noreferrer">
                                                            <VideocamOutlinedIcon />
                                                            {PROVIDER_NAMES[cand.meetingProvider] || 'Join'}
                                                        </a>
                                                        : <span className="iv-no-meeting">In person</span>}
                                                </td>
                                                <td className="wh-pin-2">
                                                    <span className={`wh-pill iv-status ${statusTone(cand.status)}`}>
                                                        {statusIcon(cand.status)}
                                                        {cand.status || 'Not Assessed'}
                                                    </span>
                                                </td>

                                                {/* both actions in one pinned column */}
                                                <td className="wh-pin-1">
                                                    <div className="wh-actions-row">
                                                        {/* the interviewer joins the same room the
                                                            candidate was emailed */}
                                                        <Tooltip
                                                            title={cand.meetingUrl
                                                                ? `Join the ${PROVIDER_NAMES[cand.meetingProvider] || 'video'} call`
                                                                : 'No meeting link — this round is not virtual'}
                                                            placement="top" arrow>
                                                            <span>
                                                                <IconButton
                                                                    className={cand.meetingUrl ? 'iv-join-btn' : ''}
                                                                    disabled={!cand.meetingUrl}
                                                                    component="a" target="_blank" rel="noreferrer"
                                                                    href={cand.meetingUrl || undefined}>
                                                                    <VideocamOutlinedIcon />
                                                                </IconButton>
                                                            </span>
                                                        </Tooltip>

                                                        <Tooltip title="View resume" placement="top" arrow>
                                                            <span>
                                                                <IconButton
                                                                    disabled={!cand.candidate?.resumeUrl}
                                                                    onClick={() => candidateResume(cand.candidate)}>
                                                                    <DescriptionOutlinedIcon />
                                                                </IconButton>
                                                            </span>
                                                        </Tooltip>

                                                        <Button variant="contained" size="small"
                                                            component={Link}
                                                            to={`/recruiter/interviewer/${encrypt(cand.candidate.candidateID)}/${encrypt(cand.interViewID)}/${encrypt(cand.scheduledTo)}`}>
                                                            Assess
                                                        </Button>
                                                    </div>
                                                </td>
                                            </tr>
                                        )
                                    })}
                                </tbody>
                            </table>
                        </div>

                        <PaginationGlobal
                            count={Math.ceil(searchResult.length / rowsPerPage)}
                            pageNumber={pageNumber} func={pull_data}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={(size) => { setRowsPerPage(size); setPageNumber(1); }} />
                    </>
                    : <NoData />
                )
            }

            <ResumeViewModal
                resumeData={resumeView}
                show={resumeShow}
                onHide={resumeHide}
            />
        </div>
    )

}

export default InterViewAsssignList;